import { LinkIcon, MessageSquareText, PinOff } from 'lucide-react';
import { Avatar } from '@/components/ui';
import { cn, timeAgo } from '@/lib/utils';
import { stripHandle } from '@/features/profile/helpers';
import type { PinnedMessage } from '../api/pins';

function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * A single pinned message rendered as a Locker-style tile: a media zone on top
 * (image, link, or text), a meta row below (author + which chat it came from).
 * The whole tile opens the source DM; the corner button unpins.
 */
export function PinCard({
  pin,
  onOpen,
  onUnpin,
}: {
  pin: PinnedMessage;
  onOpen: () => void;
  onUnpin: () => void;
}) {
  const authorName = pin.author?.display_name || stripHandle(pin.author?.handle ?? null) || 'WINR member';
  const peerName = pin.peer?.display_name || stripHandle(pin.peer?.handle ?? null) || 'a chat';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03] text-left shadow-[0_18px_50px_rgba(0,0,0,0.18)] transition-all duration-200 hover:-translate-y-0.5 hover:border-white/[0.18] hover:bg-white/[0.05]"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onUnpin();
        }}
        title="Unpin"
        aria-label="Unpin"
        className="absolute right-2 top-2 z-10 grid h-8 w-8 place-items-center rounded-full bg-black/45 text-white/80 opacity-0 backdrop-blur-md transition-all hover:bg-black/70 hover:text-white group-hover:opacity-100"
      >
        <PinOff size={15} />
      </button>

      {/* Media zone */}
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        {pin.mediaUrl ? (
          <img
            src={pin.mediaUrl}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : pin.link ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[radial-gradient(circle_at_30%_20%,rgba(84,185,254,0.22),transparent_55%),linear-gradient(135deg,rgba(255,255,255,0.06),rgba(255,255,255,0.01))] px-4 text-center">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-white/[0.08] text-accent">
              <LinkIcon size={20} />
            </span>
            <span className="line-clamp-1 text-[13px] font-bold text-white/85">{domainOf(pin.link)}</span>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center bg-[linear-gradient(135deg,rgba(255,255,255,0.05),rgba(255,255,255,0.01))] px-4">
            <p className="line-clamp-4 text-[14px] font-semibold leading-snug text-white/80">
              {pin.body || 'Pinned message'}
            </p>
            <MessageSquareText
              size={56}
              className="pointer-events-none absolute -bottom-3 -right-2 text-white/[0.05]"
            />
          </div>
        )}
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent to-black/35" />
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-2.5 px-3 py-3">
        <Avatar src={pin.author?.avatar_url ?? null} name={authorName} size={32} className="border border-white/10" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-black text-white">{authorName}</div>
          <div className="truncate text-[11px] font-semibold text-white/45">
            {pin.peer ? `Chat with ${peerName}` : 'Direct message'} · {timeAgo(pin.createdAt)}
          </div>
        </div>
        {(pin.mediaUrl || pin.link) && pin.body && (
          <span
            className={cn(
              'hidden max-w-[40%] flex-none truncate rounded-full bg-white/[0.06] px-2 py-1 text-[11px] font-semibold text-white/55 sm:block',
            )}
          >
            {pin.body}
          </span>
        )}
      </div>
    </div>
  );
}
